import { BatteryInformationDto } from '@/api/generator';
import { useAccountStore } from '@/stores/accountStore';
import {
  mdiBatteryAlertVariantOutline,
  mdiBattery10,
  mdiBattery20,
  mdiBattery30,
  mdiBattery40,
  mdiBattery50,
  mdiBattery60,
  mdiBattery70,
  mdiBattery80,
  mdiBattery90,
  mdiBattery,
  mdiBatteryUnknown,
  mdiPowerPlug,
} from '@mdi/js';
import { get } from 'lodash';
import { Ref, computed } from 'vue';
import { Duration } from 'luxon';

const icons = {
  unknown: mdiBatteryUnknown,
  poweradapter: mdiPowerPlug,
  0: mdiBatteryAlertVariantOutline,
  10: mdiBattery10,
  20: mdiBattery20,
  30: mdiBattery30,
  40: mdiBattery40,
  50: mdiBattery50,
  60: mdiBattery60,
  70: mdiBattery70,
  80: mdiBattery80,
  90: mdiBattery90,
  100: mdiBattery,
};

const thresholds = [
  { value: 60 * 60, color: 'success' },
  { value: 30 * 60, color: 'warning' },
  { value: 0, color: 'warning' },
];

function getIcon(remainingSeconds: number | undefined, capacity: number | undefined) {
  let iconKey;

  if (capacity === undefined) {
    iconKey = 'unknown';
  } else if (capacity < 0) {
    iconKey = 'poweradapter';
  } else {
    iconKey = (Math.trunc(capacity / 10) * 10).toString();
  }

  const icon = get(icons, iconKey, mdiBatteryUnknown);

  const color = remainingSeconds
    ? thresholds.find(({ value: threshold }) => remainingSeconds > threshold)?.color || ''
    : '';

  return {
    icon,
    color,
  };
}

function formatCapacity(value: number | undefined, language: string) {
  if (value === undefined) {
    return undefined;
  }

  if (value < 0) {
    return 'poweradapter';
  }

  const formatted = new Intl.NumberFormat(language, {
    maximumFractionDigits: 0,
  }).format(value);

  return `${formatted} %`;
}

function formatRemaining(seconds?: number) {
  const duration = Duration.fromObject({ seconds }).rescale().set({
    seconds: 0,
    milliseconds: 0,
  });

  // use SI units 'h' and 'min' for any later localization
  return seconds && seconds >= 3600
    ? duration.toFormat("h 'h' m 'min'")
    : duration.toFormat("m 'min'");
}

export function useDeviceBatteryInformation(
  batteryInfo: Ref<BatteryInformationDto | undefined | null>,
) {
  const accountStore = useAccountStore();

  return computed(() => {
    const capacity: number | undefined = batteryInfo.value?.currentCapacityPercent;

    const remainingSeconds = batteryInfo.value?.secondsToEmpty;

    return {
      capacity: formatCapacity(capacity, accountStore.language),
      remainingDuration: formatRemaining(remainingSeconds),
      ...getIcon(remainingSeconds, capacity),
    };
  });
}
